import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  User, Mail, X, Edit3,
  Check, Loader2, Package,
  Users, LogOut, Clock, CheckCircle, XCircle, Trash2, Gift, Eye, Crown, Award
} from 'lucide-react';
import { supabase, getSupabaseUrl } from '../supabase/client';
import { formatDateTime } from '../utils/timeUtils';
import OrderList from '../components/OrderList';

interface TeacherInfo {
  id: string;
  username: string;
  phone: string;
  teacher_nickname: string;
}

interface PackageInfo {
  id: string;
  organization: string;
  category: string;
  level_text: string;
  expire_date: string;
  package_source?: 'student_purchase' | 'admin_grant' | 'coupon_redeem';
  teacher_name?: string;
  duration_type?: 'month' | 'quarter' | 'halfYear' | 'year' | 'custom';
}


export default function UserProfile() {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loading, setLoading] = useState(true);

  const [showEditProfile, setShowEditProfile] = useState(false);
  const [showOrdersModal, setShowOrdersModal] = useState(false);
  const [showRefundDetailModal, setShowRefundDetailModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'info' | 'phone' | 'password'>('info');

  const [packages, setPackages] = useState<PackageInfo[]>([]);
  const [orderCount, setOrderCount] = useState(0);
  const [pointsBalance, setPointsBalance] = useState(0);
  const [expPoints, setExpPoints] = useState(0);

  const [teacherInfo, setTeacherInfo] = useState<TeacherInfo | null>(null);
  const [bindForm, setBindForm] = useState({ teacherPhone: '', bindCode: '' });
  const [bindLoading, setBindLoading] = useState(false);
  const [unbindLoading, setUnbindLoading] = useState(false);

  const [basicForm, setBasicForm] = useState({ username: '' });
  const [phoneForm, setPhoneForm] = useState({ newPhone: '', code: '' });
  const [passwordForm, setPasswordForm] = useState({ oldPassword: '', newPassword: '', confirmPassword: '' });
  const [passwordError, setPasswordError] = useState('');
  const [codeCountdown, setCodeCountdown] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  const [toast, setToast] = useState({ show: false, type: 'success', message: '' });
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [latestMessage, setLatestMessage] = useState<any>(null);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messages, setMessages] = useState<any[]>([]);
  const [messageLoading, setMessageLoading] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<any>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedMessageIds, setSelectedMessageIds] = useState<Set<string>>(new Set());
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [redeemLoading, setRedeemLoading] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (codeCountdown > 0) {
      const timer = setTimeout(() => setCodeCountdown(c => c - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [codeCountdown]);

  useEffect(() => {
    if (toast.show) {
      const timer = setTimeout(() => setToast({ ...toast, show: false }), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast.show]);

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) {
      const { data: accountData } = await supabase
        .from('user_accounts')
        .select('role')
        .eq('auth_user_id', session.user.id)
        .maybeSingle();

      if (accountData?.role === 'teacher') {
        navigate('/teacher');
        return;
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', session.user.id)
        .single();
      if (profile) {
        setUser({ ...profile, id: session.user.id });
        setBasicForm({ username: profile.username || '' });
        setIsLoggedIn(true);
        await loadAllData(session.user.id);
      }
    }
    setLoading(false);
  };

  const loadAllData = async (userId: string) => {
    await Promise.all([
      loadTeacherInfo(userId),
      loadPackages(userId),
      loadOrders(userId),
      loadMessageData(userId),
      loadPointsBalance(userId),
      loadExpPoints(userId),
    ]);
  };

  const loadPointsBalance = async (userId: string) => {
    try {
      const { data } = await supabase
        .from('user_point_accounts')
        .select('total_points')
        .eq('user_id', userId)
        .eq('role', 'student')
        .maybeSingle();
      setPointsBalance(data?.total_points || 0);
    } catch {
      setPointsBalance(0);
    }
  };

  const loadExpPoints = async (userId: string) => {
    try {
      const { data } = await supabase
        .from('user_point_accounts')
        .select('total_points')
        .eq('user_id', userId)
        .eq('role', 'student')
        .maybeSingle();
      setExpPoints(data?.total_points || 0);
    } catch {
      setExpPoints(0);
    }
  };

  const loadMessageData = async (userId: string) => {
    try {
      const session = (await supabase.auth.getSession()).data.session;
      const response = await fetch(`${getSupabaseUrl()}/functions/v1/message-service`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ action: 'messages_get_unread_count', userId }),
      });
      const result = await response.json();
      if (result.success) {
        setUnreadMessageCount(result.count);
      }

      const messagesResponse = await fetch(`${getSupabaseUrl()}/functions/v1/message-service`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ action: 'messages_get_messages', userId }),
      });
      const messagesResult = await messagesResponse.json();
      if (messagesResult.success) {
        const msgs = messagesResult.data || [];
        setMessages(msgs);
        if (msgs.length > 0) {
          setLatestMessage(msgs[0]);
        }
      }
    } catch {
    }
  };

  const openMessageModal = async () => {
    setShowMessageModal(true);
    setMessageLoading(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      await loadMessageData(user.id);
    }
    setMessageLoading(false);
  };

  const markAsRead = async (messageId: string) => {
    setActionLoading(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const session = await supabase.auth.getSession();
    await fetch(`${getSupabaseUrl()}/functions/v1/message-service`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.data.session?.access_token}`,
      },
      body: JSON.stringify({ action: 'messages_mark_as_read', messageId }),
    });

    setMessages(prev => prev.map(m => m.id === messageId ? { ...m, is_read: true, read_at: new Date().toISOString() } : m));
    setUnreadMessageCount(prev => Math.max(0, prev - 1));
    setActionLoading(false);

    // 通知 Header 组件更新未读数
    window.dispatchEvent(new Event('message-status-change'));
  };

  const markAllAsRead = async () => {
    setActionLoading(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const session = await supabase.auth.getSession();
    await fetch(`${getSupabaseUrl()}/functions/v1/message-service`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.data.session?.access_token}`,
      },
      body: JSON.stringify({ action: 'messages_mark_all_read', userId: user.id }),
    });

    setMessages(prev => prev.map(m => ({ ...m, is_read: true })));
    setUnreadMessageCount(0);
    setActionLoading(false);

    // 通知 Header 组件更新未读数
    window.dispatchEvent(new Event('message-status-change'));
  };

  const deleteMessage = async (messageId: string) => {
    setActionLoading(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const session = await supabase.auth.getSession();
    await fetch(`${getSupabaseUrl()}/functions/v1/message-service`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.data.session?.access_token}`,
      },
      body: JSON.stringify({ action: 'messages_delete', messageId }),
    });

    setMessages(prev => prev.filter(m => m.id !== messageId));
    const deletedMsg = messages.find(m => m.id === messageId);
    if (deletedMsg && !deletedMsg.is_read) {
      setUnreadMessageCount(prev => Math.max(0, prev - 1));
    }
    setActionLoading(false);

    // 通知 Header 组件更新未读数
    window.dispatchEvent(new Event('message-status-change'));
  };

  const toggleSelectMessage = (messageId: string) => {
    setSelectedMessageIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedMessageIds.size === messages.length) {
      setSelectedMessageIds(new Set());
    } else {
      setSelectedMessageIds(new Set(messages.map(m => m.id)));
    }
  };

  const batchMarkAsRead = async () => {
    if (selectedMessageIds.size === 0) return;
    setActionLoading(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const session = await supabase.auth.getSession();
    const selectedIds = Array.from(selectedMessageIds);
    const unreadSelected = messages.filter(m => selectedIds.includes(m.id) && !m.is_read);

    for (const messageId of selectedIds) {
      await fetch(`${getSupabaseUrl()}/functions/v1/message-service`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.data.session?.access_token}`,
        },
        body: JSON.stringify({ action: 'messages_mark_as_read', messageId }),
      });
    }

    setMessages(prev => prev.map(m => selectedIds.includes(m.id) ? { ...m, is_read: true, read_at: new Date().toISOString() } : m));
    setUnreadMessageCount(prev => Math.max(0, prev - unreadSelected.length));
    setSelectedMessageIds(new Set());
    setToast({ show: true, type: 'success', message: `已标记 ${unreadSelected.length} 条消息为已读` });
    setActionLoading(false);
    // 触发事件通知 Header 刷新未读数
    window.dispatchEvent(new Event('message-status-change'));
  };

  const batchDelete = async () => {
    if (selectedMessageIds.size === 0) return;
    setActionLoading(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const session = await supabase.auth.getSession();
    const selectedIds = Array.from(selectedMessageIds);
    const deletedUnread = messages.filter(m => selectedIds.includes(m.id) && !m.is_read);

    for (const messageId of selectedIds) {
      await fetch(`${getSupabaseUrl()}/functions/v1/message-service`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.data.session?.access_token}`,
        },
        body: JSON.stringify({ action: 'messages_delete', messageId }),
      });
    }

    setMessages(prev => prev.filter(m => !selectedIds.includes(m.id)));
    setUnreadMessageCount(prev => Math.max(0, prev - deletedUnread.length));
    setSelectedMessageIds(new Set());
    setToast({ show: true, type: 'success', message: `已删除 ${selectedIds.length} 条消息` });
    setActionLoading(false);
    // 触发事件通知 Header 刷新未读数
    window.dispatchEvent(new Event('message-status-change'));
  };

  const loadTeacherInfo = async (userId: string) => {
    const { data: bindData } = await supabase
      .from('teacher_students')
      .select('teacher_id')
      .eq('student_id', userId)
      .maybeSingle();

    if (bindData?.teacher_id) {
      const { data: teacher } = await supabase
        .from('profiles')
        .select('id, username, phone, teacher_nickname')
        .eq('id', bindData.teacher_id)
        .single();
      if (teacher) {
        setTeacherInfo({
          id: teacher.id,
          username: teacher.username || '',
          phone: teacher.phone || '',
          teacher_nickname: teacher.teacher_nickname || ''
        });
      }
    } else {
      setTeacherInfo(null);
    }
  };

  const loadPackages = async (userId: string) => {
    const today = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })();

    const { data: userPackagesData } = await supabase
      .from('user_packages')
      .select('*')
      .eq('user_id', userId)
      .eq('is_active', true)
      .gte('expire_date', today)
      .order('created_at', { ascending: true });

    // 按 (organization, category, level_text, expire_date) 去重，保留最早创建的记录
    const seen = new Set<string>();
    const uniquePackages: PackageInfo[] = [];

    for (const pkg of userPackagesData || []) {
      const key = `${pkg.organization}|${pkg.category}|${pkg.level_text}|${pkg.expire_date}`;
      if (!seen.has(key)) {
        seen.add(key);
        uniquePackages.push({
          id: pkg.id,
          organization: pkg.organization || '',
          category: pkg.category || '',
          level_text: pkg.level_text || '',
          expire_date: pkg.expire_date || '',
          package_source: (pkg.package_source as PackageInfo['package_source']) || 'student_purchase',
          duration_type: pkg.duration_type as PackageInfo['duration_type']
        });
      }
    }

    const sortedPackages = uniquePackages.sort((a, b) => {
      if (a.package_source === 'student_purchase' && b.package_source !== 'student_purchase') return -1;
      if (a.package_source !== 'student_purchase' && b.package_source === 'student_purchase') return 1;
      return new Date(a.expire_date).getTime() - new Date(b.expire_date).getTime();
    });

    setPackages(sortedPackages);
  };


  const loadOrders = async (userId: string) => {
    const { count } = await supabase
      .from('orders')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId)
      .eq('role', 'student');
    setOrderCount(count || 0);
  };

  const openRefundDetail = (order: any) => {
    setSelectedOrder(order);
    setShowRefundDetailModal(true);
  };


  const handleBindTeacher = async () => {
    if (!bindForm.teacherPhone || !bindForm.bindCode) {
      showToast('error', '请填写教师手机号和绑定码');
      return;
    }
    setBindLoading(true);
    try {
      const session = (await supabase.auth.getSession()).data.session;
      const response = await fetch(`${getSupabaseUrl()}/functions/v1/edu-service`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          action: 'bind',
          teacherPhone: bindForm.teacherPhone,
          bindCode: bindForm.bindCode,
        }),
      });
      const result = await response.json();
      if (result.success) {
        showToast('success', `绑定成功！教师：${result.teacherName}`);
        setBindForm({ teacherPhone: '', bindCode: '' });
        await loadTeacherInfo(user?.id);
        await loadPackages(user?.id);
      } else {
        showToast('error', result.message);
      }
    } catch {
      showToast('error', '绑定失败，请稍后重试');
    }
    setBindLoading(false);
  };

  const handleUnbindTeacher = async () => {
    setUnbindLoading(true);
    try {
      const session = (await supabase.auth.getSession()).data.session;
      const response = await fetch(`${getSupabaseUrl()}/functions/v1/edu-service`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ action: 'unbind' }),
      });
      const result = await response.json();
      if (result.success) {
        showToast('success', '解绑成功');
        setTeacherInfo(null);
        setPackages([]);
      } else {
        showToast('error', result.message);
      }
    } catch {
      showToast('error', '解绑失败，请稍后重试');
    }
    setUnbindLoading(false);
  };

  const handleUpdateUsername = async () => {
    if (!basicForm.username.trim()) {
      showToast('error', '用户名不能为空');
      return;
    }
    setIsSaving(true);
    try {
      const session = (await supabase.auth.getSession()).data.session;
      const response = await fetch(`${getSupabaseUrl()}/functions/v1/user-management`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          action: 'update',
          type: 'username',
          data: { username: basicForm.username },
        }),
      });
      const result = await response.json();
      if (result.success) {
        showToast('success', '用户名修改成功');
        setUser({ ...user, username: basicForm.username });
        // 触发全局事件通知 Header 刷新用户信息
        window.dispatchEvent(new Event('user-info-updated'));
      } else {
        showToast('error', result.message);
      }
    } catch {
      showToast('error', '修改失败');
    }
    setIsSaving(false);
  };

  const sendCode = async () => {
    if (!/^1[3-9]\d{9}$/.test(phoneForm.newPhone)) {
      showToast('error', '请输入正确的手机号');
      return;
    }
    setCodeCountdown(60);
    try {
      const session = (await supabase.auth.getSession()).data.session;
      await fetch(`${getSupabaseUrl()}/functions/v1/message-service`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ action: 'sms_send_code', phone: phoneForm.newPhone }),
      });
    } catch {
      showToast('error', '验证码发送失败');
    }
  };

  const handleUpdatePhone = async () => {
    if (!phoneForm.newPhone || !phoneForm.code) {
      showToast('error', '请填写完整信息');
      return;
    }
    setIsSaving(true);
    try {
      const session = (await supabase.auth.getSession()).data.session;
      const response = await fetch(`${getSupabaseUrl()}/functions/v1/user-management`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          action: 'update',
          type: 'phone',
          data: { newPhone: phoneForm.newPhone, code: phoneForm.code },
        }),
      });
      const result = await response.json();
      if (result.success) {
        showToast('success', '手机号修改成功');
        setUser({ ...user, phone: phoneForm.newPhone });
        setPhoneForm({ newPhone: '', code: '' });
        setActiveTab('info');
        // 触发全局事件通知 Header 刷新用户信息
        window.dispatchEvent(new Event('user-info-updated'));
      } else {
        showToast('error', result.message);
      }
    } catch {
      showToast('error', '修改失败');
    }
    setIsSaving(false);
  };

  const handleRedeemCoupon = async () => {
    if (!couponCode.trim()) {
      showToast('error', '请输入券码');
      return;
    }
    if (!/^\d{4}[A-Z0-9]{4}$/.test(couponCode.trim())) {
      showToast('error', '券码格式不正确，应为8位字母数字');
      return;
    }
    setRedeemLoading(true);
    try {
      const session = (await supabase.auth.getSession()).data.session;
      const response = await fetch(`${getSupabaseUrl()}/functions/v1/package-management`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          action: 'redeemCoupon',
          code: couponCode.trim(),
          userId: user?.id,
        }),
      });
      const result = await response.json();
      if (result.success) {
        showToast('success', `兑换成功！已为您开通${result.data.levelText}刷题包权限`);
        setCouponCode('');
        setShowCouponModal(false);
        await loadPackages(user?.id);
      } else {
        showToast('error', result.error || '券码无效/已使用/已作废，请检查后重试');
      }
    } catch {
      showToast('error', '兑换失败，请稍后重试');
    }
    setRedeemLoading(false);
  };

  const handleUpdatePassword = async () => {
    setPasswordError('');
    if (!passwordForm.oldPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setPasswordError('请填写完整密码信息');
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('两次密码输入不一致');
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      setPasswordError('新密码至少6位');
      return;
    }
    setIsSaving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        setPasswordError('登录状态已失效，请重新登录');
        setIsSaving(false);
        return;
      }
      const response = await fetch(`${getSupabaseUrl()}/functions/v1/user-management`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          action: 'update',
          type: 'password',
          data: { oldPassword: passwordForm.oldPassword, newPassword: passwordForm.newPassword },
        }),
      });
      const result = await response.json();
      if (result.success) {
        showToast('success', '密码修改成功');
        setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
        setActiveTab('info');
      } else {
        setPasswordError(result.message || '密码修改失败');
      }
    } catch (err) {
      console.error('密码修改异常:', err);
      setPasswordError('修改失败，请稍后重试');
    }
    setIsSaving(false);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setIsLoggedIn(false);
    navigate('/');
  };

  const showToast = (type: 'success' | 'error', message: string) => {
    setToast({ show: true, type, message });
  };

  const getDaysRemaining = (expireDate: string) => {
    const days = Math.ceil((new Date(expireDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    return days;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">加载中...</div>
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mb-4">请先登录</p>
          <Link to="/login" className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
            去登录
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50 pb-12">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* 顶部信息卡片 - 重新设计：3列布局 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {/* 用户信息卡片 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-400 to-cyan-400 flex items-center justify-center text-white text-xl font-bold">
                {(user?.username || '用').charAt(0)}
              </div>
              <div className="flex-1">
                <h1 className="font-bold text-gray-800 text-lg">{user?.username || '用户'}</h1>
                <p className="text-sm text-gray-500">
                  {(user?.phone?.includes('@') ? user.phone.split('@')[0] : user?.phone)?.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2')}
                </p>
                <button
                  onClick={() => setShowEditProfile(true)}
                  className="mt-2 text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1 transition-colors"
                >
                  <Edit3 className="w-3 h-3" />
                  修改资料
                </button>
              </div>
            </div>
          </motion.div>

          {/* 绑定教师卡片 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-5 h-5 text-purple-500" />
              <span className="font-semibold text-gray-800">绑定教师</span>
            </div>
            {teacherInfo ? (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                    <User className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-800 text-sm">{teacherInfo.teacher_nickname || teacherInfo.username}</p>
                    <p className="text-xs text-gray-500">
                      {(teacherInfo.phone?.includes('@') ? teacherInfo.phone.split('@')[0] : teacherInfo.phone)?.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2')}
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleUnbindTeacher}
                  disabled={unbindLoading}
                  className="text-sm text-red-500 hover:text-red-600 font-medium transition-colors disabled:opacity-50"
                >
                  {unbindLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : '解绑'}
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={bindForm.teacherPhone}
                    onChange={(e) => setBindForm({ ...bindForm, teacherPhone: e.target.value })}
                    placeholder="教师手机号"
                    maxLength={11}
                    className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  />
                  <input
                    type="text"
                    value={bindForm.bindCode}
                    onChange={(e) => setBindForm({ ...bindForm, bindCode: e.target.value })}
                    placeholder="绑定码"
                    maxLength={3}
                    className="w-20 px-3 py-2 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-center"
                  />
                </div>
                <button
                  onClick={handleBindTeacher}
                  disabled={bindLoading}
                  className="w-full py-2 bg-gradient-to-r from-blue-500 to-cyan-400 text-white rounded-xl font-medium text-sm hover:shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-1"
                >
                  {bindLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  绑定教师
                </button>
              </div>
            )}
          </motion.div>

          {/* 站内信卡片 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-2xl shadow-sm border border-amber-100 p-5 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center gap-2 mb-3">
              <Mail className="w-5 h-5 text-amber-600" />
              <span className="font-semibold text-gray-800">我的站内信</span>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">未读 <span className="text-amber-600 font-bold">{unreadMessageCount}</span> 条</p>
                <p className="text-xs text-gray-400 mt-1 truncate max-w-[120px]">
                  {latestMessage?.title || '暂无新消息'}
                </p>
              </div>
              <button
                onClick={openMessageModal}
                className="text-sm text-amber-600 hover:text-amber-700 font-medium transition-colors"
              >
                查看全部
              </button>
            </div>
          </motion.div>
        </div>

        {/* 快捷操作区域 - 我的订单 + 券码兑换 同一行 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-8"
        >
          <div className="flex flex-wrap items-center gap-4">
            {/* 我的订单 */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowOrdersModal(true)}
              className="flex items-center gap-2 px-6 py-4 bg-white text-gray-700 rounded-full font-semibold text-lg shadow-lg border-2 border-gray-100 hover:shadow-xl hover:border-blue-200 hover:bg-blue-50 transition-all"
            >
              我的订单
              <span className="px-3 py-1 bg-green-100 text-green-600 text-sm rounded-full">{orderCount}</span>
            </motion.button>

            {/* 我的U币 */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/point-center')}
              className="flex items-center gap-2 px-6 py-4 bg-white text-gray-700 rounded-full font-semibold text-lg shadow-lg border-2 border-gray-100 hover:shadow-xl hover:border-orange-200 hover:bg-orange-50 transition-all"
            >
              我的U币
              <span className="px-3 py-1 bg-orange-100 text-orange-600 text-sm rounded-full">{pointsBalance}</span>
            </motion.button>

            {/* 券码兑换 - 移到我的订单后面，同一行 */}
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="flex items-center gap-3 px-5 py-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-full border-2 border-purple-200 shadow-md"
            >
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                <Gift className="w-5 h-5 text-purple-600" />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  placeholder="输入8位券码"
                  maxLength={8}
                  className="w-32 px-3 py-2 rounded-lg border border-purple-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 bg-white"
                />
                <button
                  onClick={handleRedeemCoupon}
                  disabled={redeemLoading || couponCode.length !== 8}
                  className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-400 text-white rounded-lg font-medium text-sm hover:shadow-lg transition-all disabled:opacity-50 flex items-center gap-1 whitespace-nowrap"
                >
                  {redeemLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  兑换
                </button>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* 可用刷题包 - 重新设计 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
              <Package className="w-5 h-5 text-blue-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-800">可用刷题包</h3>
          </div>

          {packages.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {packages.map((pkg) => {
                const daysRemaining = getDaysRemaining(pkg.expire_date);
                const isExpiringSoon = daysRemaining <= 7;
                const isStudentPurchase = pkg.package_source === 'student_purchase';
                const isAdminGrant = pkg.package_source === 'admin_grant';
                const isCouponRedeem = pkg.package_source === 'coupon_redeem';
                const durationLabels: Record<string, string> = {
                  month: '月包',
                  quarter: '季包',
                  halfYear: '半年包',
                  year: '年包',
                  custom: '自定义包',
                };
                return (
                  <motion.div
                    key={pkg.id}
                    whileHover={{ scale: 1.02 }}
                    className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-4 border border-gray-100 hover:shadow-md transition-all"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-bold text-gray-800">
                        {pkg.level_text}
                      </span>
                      <div className="flex items-center gap-1">
                        {pkg.duration_type && (
                          <span className="px-2 py-0.5 bg-purple-100 text-purple-600 text-xs rounded-full">
                            {durationLabels[pkg.duration_type]}
                          </span>
                        )}
                        {isExpiringSoon && (
                          <span className="px-2 py-0.5 bg-red-100 text-red-600 text-xs rounded-full">
                            即将到期
                          </span>
                        )}
                        {isStudentPurchase && (
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-600 text-xs rounded-full">
                            自行购买
                          </span>
                        )}
                        {isAdminGrant && (
                          <span className="px-2 py-0.5 bg-amber-100 text-amber-600 text-xs rounded-full">
                            后台开通
                          </span>
                        )}
                        {isCouponRedeem && (
                          <span className="px-2 py-0.5 bg-pink-100 text-pink-600 text-xs rounded-full">
                            券码兑换
                          </span>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-gray-500 mb-3">
                      {pkg.organization} · {pkg.category}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className={`text-sm font-medium ${isExpiringSoon ? 'text-red-600' : 'text-gray-600'}`}>
                        {daysRemaining}天后到期
                      </span>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-gray-100 flex items-center justify-center">
                <Package className="w-10 h-10 text-gray-400" />
              </div>
              <p className="text-gray-500 font-medium mb-2">暂无可用刷题包</p>
              <p className="text-sm text-gray-400 mb-6">您可以通过购买刷题包获取刷题权限</p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/exam-categories')}
                className="px-8 py-3 bg-gradient-to-r from-blue-500 to-cyan-400 text-white rounded-full font-semibold shadow-lg shadow-blue-500/30 hover:shadow-xl hover:shadow-blue-500/40 transition-all"
              >
                去购买套餐
              </motion.button>
            </div>
          )}
        </motion.div>

        {/* 退出登录 - 重新设计 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="text-center"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleLogout}
            className="inline-flex items-center gap-2 px-8 py-3 text-red-500 font-medium rounded-full border-2 border-red-100 hover:bg-red-50 hover:border-red-200 transition-all"
          >
            <LogOut className="w-5 h-5" />
            退出登录
          </motion.button>
        </motion.div>
      </div>

      <AnimatePresence>
        {showEditProfile && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-md overflow-hidden"
            >
              <div className="flex items-center justify-between p-4 border-b border-gray-100">
                <h3 className="text-lg font-bold text-gray-800">修改资料</h3>
                <button
                  onClick={() => setShowEditProfile(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="flex border-b border-gray-100">
                <button
                  onClick={() => setActiveTab('info')}
                  className={`flex-1 py-3 text-sm font-medium transition-colors ${
                    activeTab === 'info' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'
                  }`}
                >
                  基本信息
                </button>
                <button
                  onClick={() => setActiveTab('phone')}
                  className={`flex-1 py-3 text-sm font-medium transition-colors ${
                    activeTab === 'phone' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'
                  }`}
                >
                  手机号
                </button>
                <button
                  onClick={() => setActiveTab('password')}
                  className={`flex-1 py-3 text-sm font-medium transition-colors ${
                    activeTab === 'password' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'
                  }`}
                >
                  密码
                </button>
              </div>

              <div className="p-6">
                {activeTab === 'info' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">姓名</label>
                      <input
                        type="text"
                        value={basicForm.username}
                        onChange={(e) => setBasicForm({ username: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                      />
                    </div>
                    <button
                      onClick={handleUpdateUsername}
                      disabled={isSaving}
                      className="w-full py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                      {isSaving ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : '保存'}
                    </button>
                  </div>
                )}

                {activeTab === 'phone' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">新手机号</label>
                      <input
                        type="text"
                        value={phoneForm.newPhone}
                        onChange={(e) => setPhoneForm({ ...phoneForm, newPhone: e.target.value })}
                        placeholder="请输入新手机号"
                        maxLength={11}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">验证码</label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={phoneForm.code}
                          onChange={(e) => setPhoneForm({ ...phoneForm, code: e.target.value })}
                          placeholder="请输入验证码"
                          maxLength={6}
                          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                        />
                        <button
                          onClick={sendCode}
                          disabled={codeCountdown > 0}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 whitespace-nowrap"
                        >
                          {codeCountdown > 0 ? `${codeCountdown}s` : '获取验证码'}
                        </button>
                      </div>
                    </div>
                    <button
                      onClick={handleUpdatePhone}
                      disabled={isSaving}
                      className="w-full py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                      {isSaving ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : '确认更换'}
                    </button>
                  </div>
                )}

                {activeTab === 'password' && (
                  <div className="space-y-4">
                    {passwordError && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600 flex items-center gap-2">
                        <XCircle className="w-4 h-4 shrink-0" />
                        {passwordError}
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">原密码</label>
                      <input
                        type="password"
                        value={passwordForm.oldPassword}
                        onChange={(e) => setPasswordForm({ ...passwordForm, oldPassword: e.target.value })}
                        placeholder="请输入原密码"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">新密码</label>
                      <input
                        type="password"
                        value={passwordForm.newPassword}
                        onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                        placeholder="请输入新密码（至少6位）"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">确认新密码</label>
                      <input
                        type="password"
                        value={passwordForm.confirmPassword}
                        onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                        placeholder="请再次输入新密码"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                      />
                    </div>
                    <button
                      onClick={handleUpdatePassword}
                      disabled={isSaving}
                      className="w-full py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                      {isSaving ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : '确认修改'}
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {toast.show && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className={`fixed bottom-8 left-1/2 -translate-x-1/2 px-6 py-3 rounded-lg shadow-lg z-50 ${
              toast.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
            }`}
          >
            <div className="flex items-center gap-2">
              {toast.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
              {toast.message}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showOrdersModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-[95vw] max-h-[90vh] overflow-hidden"
            >
              <div className="flex items-center justify-between p-4 border-b border-gray-100">
                <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                  <Package className="w-5 h-5 text-green-600" />
                  我的订单
                </h3>
                <button
                  onClick={() => setShowOrdersModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-4 overflow-auto max-h-[70vh]">
                <OrderList type="student-center" mode="modal" enabled={showOrdersModal} />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showRefundDetailModal && selectedOrder && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowRefundDetailModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-md overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-4 border-b border-gray-100">
                <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                  <Eye className="w-5 h-5 text-blue-500" />
                  退款详情
                </h3>
                <button
                  onClick={() => setShowRefundDetailModal(false)}
                  className="p-1 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-4 space-y-4">
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">订单号</span>
                    <span className="font-mono">{selectedOrder.orderNo}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">套餐名称</span>
                    <span>{selectedOrder.orgName && selectedOrder.orgName !== '-' ? `${selectedOrder.orgName} / ${selectedOrder.catName || selectedOrder.category} / ${selectedOrder.levelText}` : '-'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">实付金额</span>
                    <span className="text-gray-800">¥{(selectedOrder.actualPrice != null ? Number(selectedOrder.actualPrice) : Number(selectedOrder.price || 0)).toFixed(2)}</span>
                  </div>
                </div>
                <div className="border-t border-gray-200 pt-4 space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-800">已退款成功</div>
                      <div className="text-xs text-gray-500">{formatDateTime(selectedOrder.refundedAt || selectedOrder.updatedAt)}</div>
                    </div>
                  </div>
                  <div className="ml-4 pl-4 border-l-2 border-green-200 space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">退款金额</span>
                      <span className="font-bold text-red-600">¥{selectedOrder.refundAmount || selectedOrder.price}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">退款去向</span>
                      <span className="text-gray-800">{selectedOrder.refundDestination === 'balance' ? '退回平台余额' : '原路退回'}</span>
                    </div>
                    {selectedOrder.refundReason && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">退款原因</span>
                        <span className="text-gray-800 max-w-[200px] text-right">{selectedOrder.refundReason}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showMessageModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowMessageModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-2xl max-h-[80vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-4 border-b border-gray-100">
                <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                  <Mail className="w-5 h-5 text-amber-600" />
                  我的站内信
                  {unreadMessageCount > 0 && (
                    <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">
                      未读 {unreadMessageCount} 条
                    </span>
                  )}
                </h3>
                <button
                  onClick={() => setShowMessageModal(false)}
                  className="p-1 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              {messages.length > 0 && (
                <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-100">
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedMessageIds.size === messages.length && messages.length > 0}
                        onChange={toggleSelectAll}
                        className="w-4 h-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                      />
                      <span className="text-sm text-gray-600">
                        已选择 {selectedMessageIds.size} 条
                      </span>
                    </label>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={batchMarkAsRead}
                      disabled={actionLoading || selectedMessageIds.size === 0}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Check className="w-4 h-4" />
                      批量标记已读
                    </button>
                    <button
                      onClick={batchDelete}
                      disabled={actionLoading || selectedMessageIds.size === 0}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Trash2 className="w-4 h-4" />
                      批量删除
                    </button>
                  </div>
                </div>
              )}
              <div className="overflow-y-auto max-h-[60vh]">
                {messageLoading ? (
                  <div className="flex items-center justify-center h-48">
                    <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
                  </div>
                ) : messages.length > 0 ? (
                  <div className="divide-y divide-gray-100">
                    {messages.map((message) => (
                      <div
                        key={message.id}
                        className={`p-4 hover:bg-gray-50 transition-colors cursor-pointer ${
                          !message.is_read ? 'bg-amber-50/50' : ''
                        }`}
                        onClick={() => {
                          setSelectedMessage(message);
                          if (!message.is_read) markAsRead(message.id);
                        }}
                      >
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            checked={selectedMessageIds.has(message.id)}
                            onChange={(e) => {
                              e.stopPropagation();
                              toggleSelectMessage(message.id);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="w-4 h-4 mt-3 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                          />
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            message.is_read ? 'bg-gray-100' : 'bg-amber-100'
                          }`}>
                            <Mail className={`w-5 h-5 ${message.is_read ? 'text-gray-500' : 'text-amber-600'}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h4 className={`font-medium truncate ${message.is_read ? 'text-gray-700' : 'text-gray-900'}`}>
                                {message.title}
                              </h4>
                              {!message.is_read && (
                                <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 mt-1 line-clamp-1">{message.content}</p>
                            <div className="flex items-center gap-1 mt-2 text-xs text-gray-400">
                              <Clock className="w-3 h-3" />
                              {formatDateTime(message.created_at)}
                            </div>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteMessage(message.id);
                            }}
                            disabled={actionLoading}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-12 text-center">
                    <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                      <Mail className="w-8 h-8 text-amber-600" />
                    </div>
                    <p className="text-gray-500">暂无消息</p>
                    <p className="text-sm text-gray-400 mt-1">系统消息将在这里显示</p>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showCouponModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowCouponModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-md overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-4 border-b border-gray-100">
                <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                  <Gift className="w-5 h-5 text-purple-600" />
                  券码兑换
                </h3>
                <button
                  onClick={() => setShowCouponModal(false)}
                  className="p-1 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    请输入8位券码
                  </label>
                  <input
                    type="text"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    placeholder="如：2026D98M"
                    maxLength={8}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none text-center text-lg tracking-wider font-mono"
                  />
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    券码格式：4位年份 + 4位字母数字
                  </p>
                </div>
                <button
                  onClick={handleRedeemCoupon}
                  disabled={redeemLoading || couponCode.length !== 8}
                  className="w-full py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {redeemLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Check className="w-5 h-5" />
                  )}
                  提交兑换
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {selectedMessage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
            onClick={() => setSelectedMessage(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-lg overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-4 border-b border-gray-100">
                <h3 className="text-lg font-bold text-gray-800">{selectedMessage.title}</h3>
                <button
                  onClick={() => setSelectedMessage(null)}
                  className="p-1 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-4">
                <p className="text-gray-700 whitespace-pre-wrap">{selectedMessage.content}</p>
                <div className="flex items-center gap-2 mt-4 text-sm text-gray-400">
                  <Clock className="w-4 h-4" />
                  {formatDateTime(selectedMessage.created_at)}
                  {selectedMessage.is_read && (
                    <>
                      <CheckCircle className="w-4 h-4 text-green-500 ml-2" />
                      <span className="text-green-600">已读</span>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
